/**
 * Selection action hooks for document state management
 *
 * These hooks provide a Zustand-style API for selection actions.
 * They dispatch directly to the Zustand DocumentStore via the DocumentStore,
 * bypassing Redux entirely.
 *
 * Selection actions do not trigger saves or action records (they have
 * shouldGenerateActionRecord: false and updatesDocumentTimestamp: false),
 * so they can safely use Zustand directly without needing Redux middleware.
 */
import {SceneManager} from "export";
import {difference} from "lodash";
import {useCallback} from "react";

import {useFluxServices} from "injection/hooks";
import type {DocumentStore} from "modules/document/state/document";

/**
 * Hook to get the selectObjects action
 *
 * Dispatches directly to Zustand via DocumentStore, bypassing Redux.
 * Selection actions don't need middleware (no action records, no save triggers).
 *
 * @returns A function to select objects by their UIDs
 */
export function useSelectObjects(): (selectedObjectUids: string[], userHasPermission?: boolean) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (selectedObjectUids: string[], userHasPermission?: boolean) => {
            documentStore.getState().selectObjects(selectedObjectUids, userHasPermission);
        },
        [documentStore],
    );
}

/**
 * Hook to get the setSelection action
 *
 * Sets the current selection to the specified subjects, along with their related subjects as appropriate.
 * Dispatches directly to Zustand via DocumentStore.
 *
 * @returns A function to set the selection with related subjects
 */
export function useSetSelection(): (
    selectedObjectUids: string[],
    userHasPermission?: boolean,
    sceneManager?: SceneManager | null,
) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (selectedObjectUids: string[], userHasPermission?: boolean, sceneManager?: SceneManager | null) => {
            const stateManager = sceneManager?.getStateManager();
            const related = (stateManager?.getRelatedSubjectsToSelect(selectedObjectUids) ?? []).map(
                (s) => s.subjectUid,
            );
            const finalSelection = [...selectedObjectUids, ...related];
            documentStore.getState().selectObjects(finalSelection, userHasPermission);
        },
        [documentStore],
    );
}

/**
 * Hook to get the modifySelection action
 *
 * Adds and removes the specified subjects to/from the selection, along with their related subjects as appropriate.
 * Dispatches directly to Zustand via DocumentStore.
 *
 * @returns A function to modify the selection by adding and removing subjects
 */
export function useModifySelection(): (
    addToSelectionUids: string[],
    removeFromSelectionUids: string[],
    userHasPermission?: boolean,
    sceneManager?: SceneManager | null,
) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (
            addToSelectionUids: string[],
            removeFromSelectionUids: string[],
            userHasPermission?: boolean,
            sceneManager?: SceneManager | null,
        ) => {
            const stateManager = sceneManager?.getStateManager();
            const toDeselect = (stateManager?.getRelatedSubjectsToDeselect(removeFromSelectionUids) ?? []).map(
                (s) => s.subjectUid,
            );
            const toSelect = (stateManager?.getRelatedSubjectsToSelect(addToSelectionUids) ?? []).map(
                (s) => s.subjectUid,
            );

            // Read current selection from Zustand store at execution time
            const previouslySelectedUids = documentStore.getState().document?.selectedObjectUids ?? [];
            const selectedObjectUids = difference(previouslySelectedUids, [...removeFromSelectionUids, ...toDeselect]);
            selectedObjectUids.push(...addToSelectionUids, ...toSelect);

            documentStore.getState().selectObjects(selectedObjectUids, userHasPermission);
        },
        [documentStore],
    );
}

/**
 * Factory function to create selection actions for non-React contexts
 *
 * Use this in helper classes, services, or other non-React modules that need
 * to dispatch selection actions.
 *
 * @param documentStore - The document store from FluxServices
 * @returns An object with selection action methods
 */
export function createSelectionActions(documentStore: DocumentStore) {
    return {
        selectObjects: (selectedObjectUids: string[], userHasPermission?: boolean) => {
            documentStore.getState().selectObjects(selectedObjectUids, userHasPermission);
        },
        setSelection: (
            selectedObjectUids: string[],
            userHasPermission?: boolean,
            sceneManager?: SceneManager | null,
        ) => {
            const stateManager = sceneManager?.getStateManager();
            const related = (stateManager?.getRelatedSubjectsToSelect(selectedObjectUids) ?? []).map(
                (s) => s.subjectUid,
            );
            const finalSelection = [...selectedObjectUids, ...related];
            documentStore.getState().selectObjects(finalSelection, userHasPermission);
        },
        modifySelection: (
            addToSelectionUids: string[],
            removeFromSelectionUids: string[],
            userHasPermission?: boolean,
            sceneManager?: SceneManager | null,
        ) => {
            const stateManager = sceneManager?.getStateManager();
            const toDeselect = (stateManager?.getRelatedSubjectsToDeselect(removeFromSelectionUids) ?? []).map(
                (s) => s.subjectUid,
            );
            const toSelect = (stateManager?.getRelatedSubjectsToSelect(addToSelectionUids) ?? []).map(
                (s) => s.subjectUid,
            );

            const previouslySelectedUids = documentStore.getState().document?.selectedObjectUids ?? [];
            const selectedObjectUids = difference(previouslySelectedUids, [...removeFromSelectionUids, ...toDeselect]);
            selectedObjectUids.push(...addToSelectionUids, ...toSelect);

            documentStore.getState().selectObjects(selectedObjectUids, userHasPermission);
        },
    };
}
